/**
 * Umbraco backoffice preview support.
 *
 * The CMS redirects its preview frame to this app with a short-lived token in the
 * `umbPreview` query parameter (see `HeadlessPreviewRedirectContentFinder` on the Umbraco
 * side). Echoing that token back on Delivery API calls is what makes the API return draft
 * content instead of the published version.
 *
 * The token is captured once, at module load, before React Router gets a chance to touch the
 * URL, then kept for the rest of the tab session so it survives client-side navigation.
 *
 * Names here must stay in sync with `HeadlessPreviewConstants` in
 * `backend/UmbracoHeadless.Web/Configuration/HeadlessPreviewOptions.cs`.
 */

const TOKEN_QUERY_PARAM = 'umbPreview';
const TOKEN_HEADER = 'X-Umb-Preview-Token';
const SESSION_STORAGE_KEY = 'umb:previewToken';

const readStoredToken = (): string | null => {
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    // Private browsing and sandboxed frames can both refuse storage access.
    return null;
  }
};

const storeToken = (token: string): void => {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, token);
  } catch {
    // Non-fatal: the in-memory copy still covers this page load.
  }
};

/**
 * Removes the token from the address bar. It stays available in memory and session storage,
 * but is kept out of anything that gets copied or forwarded — shared links, the Referer
 * header, analytics page URLs — because it grants draft-content access until it expires.
 */
const stripTokenFromUrl = (params: URLSearchParams): void => {
  params.delete(TOKEN_QUERY_PARAM);
  const query = params.toString();

  try {
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
  } catch {
    // Some sandboxed frames disallow history manipulation; the token simply stays visible.
  }
};

const captureToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get(TOKEN_QUERY_PARAM);

  // A reload inside the preview frame arrives without the query parameter, because the first
  // load stripped it, so fall back to what that first load stored.
  if (!tokenFromUrl) {
    return readStoredToken();
  }

  storeToken(tokenFromUrl);
  stripTokenFromUrl(params);

  return tokenFromUrl;
};

const previewToken: string | null = captureToken();

/** True when this page load is an Umbraco preview and should render draft content. */
export const isPreviewMode = (): boolean => previewToken !== null;

/**
 * Headers that unlock draft content on the Delivery API. The CMS exchanges this token for the
 * real Delivery API key server-side, so no API key is ever present in the browser.
 */
export const previewHeaders = (): Record<string, string> =>
  previewToken ? { [TOKEN_HEADER]: previewToken } : {};
